/*
 * product-quick-view.js — drives the shared quick-view overlay for the "Shop by Gear"
 * (bst-card) cards on the homepage, collection and search pages.
 *
 * Markup: snippets/product-quick-view.liquid (mounted once with mount: 'fixed')
 * Styles: assets/product-quick-view.css
 *
 * The bundle builder drives the very same markup from assets/bundle-builder.js, with its
 * own bundle-specific pricing and an "Add to bundle" action. Only ever one instance is
 * open, and each driver owns the instance inside its own subtree.
 *
 * The card paints the overlay from data-* attributes so it appears instantly, then fills
 * the gallery and the long description from /products/<handle>.js. Dimensions, weight and
 * pack contents come from data-* too — they are metafields, which product.js does not
 * return.
 */
(function () {
  'use strict';

  var root = null;
  var el = {};
  var opener = null;
  var busy = false;
  var addTimer = null;
  var cache = {};

  function hook(name) {
    return root.querySelector('[data-pqv-' + name + ']');
  }

  function boot() {
    if (root) return true;
    root = document.querySelector('.pqv--fixed[data-pqv]');
    if (!root) return false;
    el = {
      thumbs: hook('thumbs'),
      prev: hook('prev'),
      next: hook('next'),
      title: hook('title'),
      price: hook('price'),
      was: hook('was'),
      blurb: hook('blurb'),
      specs: hook('specs'),
      specsPanel: hook('panel-specs'),
      pack: hook('pack'),
      packPanel: hook('panel-pack'),
      bodyPanel: hook('body'),
      rte: hook('rte'),
      add: hook('add'),
      full: hook('full'),
      variants: hook('variants')
    };
    // Scroll containers have no data hook of their own; they are reset, never read.
    el.dialog = root.querySelector('.pqv__dialog');
    el.info = root.querySelector('.pqv__info');
    // Gestures are published as events by the shared layer at the bottom of this file.
    root.addEventListener('pqv:close', close);
    return true;
  }

  /* Own attribute rather than the theme's shared html[scroll-lock] (see the rule in
     product-quick-view.css for why): dialog.js removes the shared one whenever any
     <details scroll-lock> closes, which dropped the lock while this overlay was still open.
     Owning it also means we never release a lock the cart drawer or search modal still needs. */
  var LOCK = 'pqv-scroll-lock';

  function lockScroll(on) {
    var html = document.documentElement;
    if (on) {
      html.setAttribute(LOCK, '');
    } else {
      html.removeAttribute(LOCK);
    }
  }

  function label(key) {
    return root.getAttribute('data-l-' + key) || '';
  }

  /* Ask the CDN for the width actually needed rather than the stored original. */
  function sized(url, w) {
    if (!url) return '';
    return (url.indexOf('?') > -1 ? url.split('?')[0] : url) + '?width=' + w;
  }

  function parseVideo(u) {
    if (typeof u !== 'string' || u.indexOf('video:') !== 0) return null;
    var parts = u.slice(6).split('::');
    return { src: parts[0], poster: parts[1] || '' };
  }

  /* The carousel owns the photos now; this is just the driver's handle on it. Resolved
     lazily because the controller lives further down this file. */
  function gal() {
    return (root && window.pqvGallery) ? window.pqvGallery(root) : null;
  }

  function show(i) {
    var g = gal();
    if (g) g.go(i);
  }

  function setGallery(list, altText) {
    var g = gal();
    // 900px was fine for a 393px photo on a laptop; on a wide monitor (photo up to 680px) or a
    // retina screen the same file is upscaled and reads as soft / zoomed. The CDN never upscales,
    // so asking for 1600 just returns the original where it is smaller.
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var big = window.innerWidth >= 1600 || dpr > 1.5;
    if (g) g.set(list, altText, big ? 1600 : 900);
  }

  /* Dimensions and weight only — the same two rows the builder shows. */
  function setSpecs(d) {
    if (!el.specs) return;
    el.specs.textContent = '';
    var rows = [[label('dims'), d.dims], [label('weight'), d.weight]];
    var n = 0;
    rows.forEach(function (r) {
      if (!r[1]) return;
      var dt = document.createElement('dt');
      dt.textContent = r[0];
      var dd = document.createElement('dd');
      dd.textContent = r[1];
      el.specs.appendChild(dt);
      el.specs.appendChild(dd);
      n++;
    });
    if (el.specsPanel) el.specsPanel.hidden = n === 0;
  }

  function setPack(raw) {
    if (!el.pack) return;
    el.pack.textContent = '';
    var items = (raw || '').split('|').filter(function (t) { return t.trim(); });
    items.forEach(function (t) {
      var li = document.createElement('li');
      li.textContent = t.trim();
      el.pack.appendChild(li);
    });
    if (el.packPanel) el.packPanel.hidden = items.length === 0;
  }

  function syncAdd(state) {
    if (!el.add) return;
    /* „Sestav si svůj FAR" card: the main button opens the build page instead of adding the
       bare bag, so it never shows "added" / "sold out". */
    if (root && root.dataset.mode === 'build') {
      el.add.textContent = root.dataset.cta || label('add');
      el.add.classList.remove('is-in');
      el.add.disabled = false;
      return;
    }
    if (state === 'added') {
      el.add.textContent = label('added');
      el.add.classList.add('is-in');
      el.add.disabled = false;
      return;
    }
    if (state === 'soldout') {
      el.add.textContent = label('soldout');
      el.add.classList.remove('is-in');
      el.add.disabled = true;
      return;
    }
    el.add.textContent = label('add');
    el.add.classList.remove('is-in');
    el.add.disabled = false;
  }

  /* ── Varianty (vzor vollyc.com) ─────────────────────────────────────────────
     Skupina pilulek per opce. Vybraná kombinace přepisuje root.dataset.variant, cenu,
     dostupnost tlačítka a přeskočí galerii na fotku varianty. Ceny chodí z product.js
     v centech, formátují se přes shop.money_format z data-money-format. */

  function formatMoney(cents, format) {
    var value = cents / 100;
    function num(dec, thou, decSep) {
      var parts = value.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return parts.length > 1 ? parts[0] + decSep + parts[1] : parts[0];
    }
    return (format || '{{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, num(2, ',', '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, num(0, ',', '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, num(2, '.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, num(0, '.', ','))
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/, num(2, ' ', ','))
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/, num(0, ' ', ','))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, num(2, "'", '.'));
  }

  var selOpts = [];   // zvolené hodnoty opcí (indexy = pozice opce)

  function findVariant(p) {
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      var ok = true;
      for (var o = 0; o < selOpts.length; o++) {
        if (selOpts[o] != null && v.options[o] !== selOpts[o]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  /* Dostupnost jedné hodnoty při aktuální volbě OSTATNÍCH opcí - kvůli přeškrtnutí
     vyprodaných pilulek. */
  function valueAvailable(p, optIndex, value) {
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      if (v.options[optIndex] !== value) continue;
      var ok = true;
      for (var o = 0; o < selOpts.length; o++) {
        if (o === optIndex) continue;
        if (selOpts[o] != null && v.options[o] !== selOpts[o]) { ok = false; break; }
      }
      if (ok && v.available) return true;
    }
    return false;
  }

  function applyVariant(p, v) {
    if (!v) return;
    root.dataset.variant = String(v.id);
    if (el.price) el.price.textContent = formatMoney(v.price, root.getAttribute('data-money-format'));
    if (el.was) {
      var was = v.compare_at_price && v.compare_at_price > v.price;
      el.was.textContent = was ? formatMoney(v.compare_at_price, root.getAttribute('data-money-format')) : '';
      el.was.hidden = !was;
    }
    syncAdd(v.available ? 'idle' : 'soldout');
    // Fotka varianty: product.js dává featured_image.src, images jsou URL - páruje se
    // přes cestu bez query, protože CDN parametry se liší.
    if (v.featured_image && v.featured_image.src) {
      var key = v.featured_image.src.split('?')[0];
      var imgs = (cache[root.dataset.handle] || p).images || [];
      for (var i = 0; i < imgs.length; i++) {
        if ((imgs[i] || '').split('?')[0] === key) {
          var g = gal();
          if (g) g.go(Math.min(i, g.count() - 1));
          break;
        }
      }
    }
  }

  function paintVariantUI(p) {
    if (!el.variants) return;
    Array.prototype.forEach.call(el.variants.querySelectorAll('.pqv__opt[data-opt-index]'), function (grp) {
      var oi = parseInt(grp.getAttribute('data-opt-index'), 10);
      var valEl = grp.querySelector('[data-pqv-opt-val]');
      if (valEl) valEl.textContent = selOpts[oi] || '';
      Array.prototype.forEach.call(grp.querySelectorAll('.pqv__opt-btn'), function (b) {
        var val = b.getAttribute('data-value');
        b.classList.toggle('is-selected', val === selOpts[oi]);
        b.classList.toggle('is-oos', !valueAvailable(p, oi, val));
      });
    });
  }

  /* ── Velikosti jako sourozenecké produkty ──────────────────────────────────────────────
     Packing Cube S / M Pillow / L / XL (a další řady) jsou samostatné produkty, product.js zná
     jen svou velikost. Mapu sourozenců tiskne Liquid do <script data-pqv-families> (stejná
     pravidla jako přepínač na PDP). Pilulky velikostí přepnou náhled na jiný produkt: hlavička,
     cena, odkaz, parametry a obsah balení z mapy hned, galerie a popis pak z product.js. */
  var families = null;
  function famMap() {
    if (families) return families;
    families = {};
    var tag = root && root.querySelector('[data-pqv-families]');
    if (tag) { try { families = JSON.parse(tag.textContent || '{}') || {}; } catch (e) { families = {}; } }
    return families;
  }
  function siblingsOf(handle) {
    var map = famMap(), me = map[handle];
    if (!me) return null;
    var list = [];
    for (var h in map) { if (map[h].g === me.g) { map[h].h = h; list.push(map[h]); } }
    if (list.length < 2) return null;
    list.sort(function (a, b) { return a.o - b.o; });
    return { me: me, list: list };
  }
  function switchProduct(sib) {
    if (!root || root.dataset.handle === sib.h) return;
    root.dataset.handle = sib.h;
    root.dataset.variant = sib.v || '';
    if (el.title) el.title.textContent = sib.t || '';
    if (el.blurb) el.blurb.textContent = sib.d || '';
    if (el.price) el.price.textContent = sib.p || '';
    if (el.was) { el.was.textContent = sib.w || ''; el.was.hidden = !sib.w; }
    if (el.full) el.full.href = sib.u || '#';
    setSpecs({ dims: sib.dm, weight: sib.wg });
    setPack(sib.pk);
    setGallery([sib.i], sib.t || '');
    if (el.rte) el.rte.innerHTML = '';
    if (el.bodyPanel) el.bodyPanel.hidden = true;
    syncAdd(sib.a === false ? 'soldout' : 'idle');
    if (el.info) el.info.scrollTop = 0;
    load(sib.h);
  }
  function renderFamily(handle) {
    var fam = siblingsOf(handle);
    if (!fam) return false;
    var grp = document.createElement('div');
    grp.className = 'pqv__opt pqv__opt--family';
    var lab = document.createElement('div');
    lab.className = 'pqv__opt-label';
    lab.textContent = fam.me.gl || '';
    grp.appendChild(lab);
    var row = document.createElement('div');
    row.className = 'pqv__opt-values';
    fam.list.forEach(function (sib) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'pqv__opt-btn' + (sib.h === handle ? ' is-selected' : '') + (sib.a === false ? ' is-oos' : '');
      b.setAttribute('data-value', sib.s);
      b.setAttribute('aria-pressed', sib.h === handle ? 'true' : 'false');
      b.textContent = sib.l || sib.s;
      b.addEventListener('click', function () { switchProduct(sib); });
      row.appendChild(b);
    });
    grp.appendChild(row);
    el.variants.appendChild(grp);
    return true;
  }

  function renderVariants(p) {
    if (!el.variants) return;
    el.variants.textContent = '';
    var hasFamily = renderFamily(root.dataset.handle);
    // Jednohodnotová opce (každý velikostní produkt má svou jedinou "Size") nic nenabízí —
    // stejná pojistka jako ve variant-main-picker na PDP. Rodina nahoře ji nahrazuje.
    var opts = (p.options || []).filter(function (o) { return (o.values || []).length > 1; });
    var real = p.variants && p.variants.length > 0 && opts.length > 0 &&
      !(p.variants.length === 1 && p.variants[0].title === 'Default Title');
    if (!real) { el.variants.hidden = !hasFamily; return; }

    var vid = Number(root.dataset.variant);
    var current = null;
    p.variants.forEach(function (v) { if (v.id === vid) current = v; });
    if (!current) {
      current = p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];
    }
    selOpts = current.options.slice();

    (p.options || []).forEach(function (opt, oi) {
      if ((opt.values || []).length < 2) return;
      var grp = document.createElement('div');
      grp.className = 'pqv__opt';
      grp.setAttribute('data-opt-index', String(oi));
      var lab = document.createElement('div');
      lab.className = 'pqv__opt-label';
      lab.textContent = (opt.name || '') + ': ';
      var labVal = document.createElement('span');
      labVal.setAttribute('data-pqv-opt-val', '');
      lab.appendChild(labVal);
      grp.appendChild(lab);
      var row = document.createElement('div');
      row.className = 'pqv__opt-values';
      (opt.values || []).forEach(function (val) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'pqv__opt-btn';
        b.setAttribute('data-value', val);
        b.textContent = val;
        b.addEventListener('click', function () {
          selOpts[oi] = val;
          var v = findVariant(p);
          if (!v) {
            // Kombinace neexistuje - vezmi první variantu s touto hodnotou a přepni
            // zbylé opce podle ní, ať volba nikdy neskončí ve slepé uličce.
            for (var i = 0; i < p.variants.length; i++) {
              if (p.variants[i].options[oi] === val) { v = p.variants[i]; break; }
            }
            if (v) selOpts = v.options.slice();
          }
          paintVariantUI(p);
          applyVariant(p, v);
        });
        row.appendChild(b);
      });
      grp.appendChild(row);
      el.variants.appendChild(grp);
    });

    el.variants.hidden = false;
    paintVariantUI(p);
    applyVariant(p, current);
  }

  /* Photos AND Shopify-hosted videos, in the product's own media order (product.js `media`).
     A video becomes "video:<mp4>::<poster>" - the gallery renders it with controls, the same
     item the „Sestav si svůj FAR" card uses. Falls back to the plain image list. */
  function galleryList(p) {
    if (!p.media || !p.media.length) return p.images || [];
    var out = [];
    p.media.forEach(function (m) {
      if (m.media_type === 'image' && m.src) out.push(m.src);
      else if (m.media_type === 'video' && m.sources && m.sources.length) {
        var best = null;
        m.sources.forEach(function (src) {
          if (src.format === 'mp4' && src.height <= 1080 && (!best || src.height > best.height)) best = src;
        });
        if (best) out.push('video:' + best.url + '::' + ((m.preview_image && m.preview_image.src) || ''));
      }
    });
    return out.length ? out : (p.images || []);
  }

  function load(handle) {
    if (!handle) return;
    var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    var done = function (p) {
      // A second click while another product is open must not repaint this one.
      if (!root || root.hidden || root.dataset.handle !== handle) return;
      root.classList.remove('is-pending');
      // Build mode keeps the card's own photo set and has no variant picker: the version
      // (Travel / Moto) is chosen on the build page.
      var isBuild = root.dataset.mode === 'build';
      if (!isBuild) {
        try { setGallery(galleryList(p).slice(0, 8), p.title || ''); } catch (e) {}
      }
      if (el.rte && el.bodyPanel) {
        el.rte.innerHTML = p.description || '';
        el.bodyPanel.hidden = !p.description;
      }
      if (!isBuild) {
        try { renderVariants(p); } catch (e) {}
      }
    };
    if (cache[handle]) { done(cache[handle]); return; }
    fetch(rootUrl + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('pqv ' + r.status); return r.json(); })
      .then(function (p) { cache[handle] = p; done(p); })
      .catch(function () { if (root) root.classList.remove('is-pending'); /* the data-* paint already stands on its own */ });
  }

  /*
   * ── Opening from inside a native <dialog> (the search modal) ──────────────────────────
   *
   * Left in <body> the overlay is invisible there: a modal <dialog> renders in the browser's
   * top layer, which no z-index can climb over (this overlay sits at 2147483100 and still
   * loses). Two attempts that did NOT work, so nobody repeats them:
   *
   *   1. Dismiss the search first. Visible, but it threw the shopper's query away — closing
   *      the preview left them on a bare page instead of back in their results.
   *   2. Move the overlay into the search dialog. It does reach the top layer, but Chromium
   *      treats a top-layer element as the containing block for its `position: fixed`
   *      descendants, so the overlay sized itself to the dialog and got clipped by its
   *      `overflow: hidden` — measured 101,126 1238×638 instead of 0,0 1440×900, with the
   *      title row cut off and no close button on screen. Killing the dialog's transform
   *      (the usual containing-block culprit) changed nothing; the top layer alone does it.
   *
   * So the overlay gets its OWN <dialog> — the shell — and that is opened with showModal().
   * Two modal dialogs stack in the top layer with the newer one on top, the shell is sized
   * to the viewport, and the overlay inside it is therefore full-screen again. The search
   * dialog stays open and untouched underneath: query, results and scroll position all
   * survive for when the preview closes.
   *
   * Keeping the shell a SEPARATE subtree (not a child of the search dialog) is what makes
   * the key handling free: dialog.js binds its Escape and click-outside handlers to the
   * search <dialog> element, and events inside the shell never pass through it. Escape
   * therefore closes the preview only, and a click on the preview's backdrop no longer
   * reads as "clicked outside the search".
   */
  var shell = null;

  function mountInTopLayer(btn) {
    /* On an ordinary page there is no top layer to out-stack: the overlay stays in <body>,
       exactly as the homepage and collection cards have always used it. */
    if (!root || !btn.closest('dialog[open]')) return;
    if (!shell) {
      shell = document.createElement('dialog');
      shell.className = 'pqv-shell';
      /* Escape is left to the platform (see the keydown handler for why), so the shell can
         close without this driver knowing. Unwind from its `close` event, or the overlay
         would stay flagged open with html[pqv-scroll-lock] still set. */
      shell.addEventListener('close', function () { if (root && !root.hidden) close(); });
      document.body.appendChild(shell);
    }
    shell.appendChild(root);
    if (!shell.open) shell.showModal();
  }

  function restoreFromTopLayer() {
    if (!shell) return;
    if (root && root.parentElement === shell) document.body.appendChild(root);
    if (shell.open) shell.close();
  }

  function open(btn) {
    if (!boot()) return;
    mountInTopLayer(btn);
    var d = btn.dataset;
    opener = btn;
    root.dataset.handle = d.handle || '';
    root.dataset.variant = d.variant || '';
    root.dataset.mode = d.mode || '';
    root.dataset.cta = d.cta || '';
    root.dataset.build = d.build || '';

    if (el.title) el.title.textContent = d.title || '';
    if (el.blurb) el.blurb.textContent = d.desc || '';
    if (el.price) el.price.textContent = d.price || '';
    if (el.was) {
      el.was.textContent = d.was || '';
      el.was.hidden = !d.was;
    }
    if (el.full) el.full.href = d.url || '#';

    /* The action row is painted FIRST and the data painters below are each guarded. They read
       data-* payloads built from metafields (specs, pack contents), and one malformed payload
       used to throw halfway through open(), leaving the button with no label - which reads as
       "the Add to cart button and the product link are missing". Nothing optional may be able
       to take the two things every quick view must have. */
    syncAdd(d.available === 'false' ? 'soldout' : 'idle');

    try { setSpecs(d); } catch (e) {}
    try { setPack(d.pack); } catch (e) {}
    // Reset the gallery to the card's own photo so nothing from the previous product shows
    // through while product.js is in flight.
    try { setGallery(d.imgs ? d.imgs.split('|').filter(Boolean) : [d.img], d.title || ''); } catch (e) {}
    if (el.rte) el.rte.innerHTML = '';
    if (el.bodyPanel) el.bodyPanel.hidden = true;
    if (el.variants) { el.variants.textContent = ''; el.variants.hidden = true; }

    root.classList.toggle('is-pending', !!d.handle);
    root.hidden = false;
    lockScroll(true);
    if (el.dialog) el.dialog.scrollTop = 0;
    if (el.info) el.info.scrollTop = 0;
    var closeBtn = root.querySelector('button[data-pqv-close]');
    if (closeBtn) closeBtn.focus();

    load(d.handle);
  }

  function close() {
    if (!root || root.hidden) return;
    root.querySelectorAll('video').forEach(function (v) { v.pause(); });
    root.hidden = true;
    root.classList.remove('pqv--expanded');
    lockScroll(false);
    if (addTimer) { clearTimeout(addTimer); addTimer = null; }
    /* Hidden first, then moved: shifting a visible node between parents flashes. */
    restoreFromTopLayer();
    var back = opener;
    opener = null;
    if (back && back.isConnected) back.focus();
  }

  function addToCart() {
    if (!root || busy) return;
    var id = root.dataset.variant;
    if (!id) return;
    busy = true;
    var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    // Optimistic, same as the card chip: flip the label at once, revert only on failure.
    syncAdd('added');
    if (addTimer) clearTimeout(addTimer);
    addTimer = setTimeout(function () { syncAdd('idle'); }, 1800);

    fetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add ' + r.status); return r.json(); })
      .then(function () { return fetch(rootUrl + 'cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: null, sourceId: 'pqv', data: { source: 'pqv', itemCount: cart.item_count } }
        }));
      })
      .catch(function () {
        if (addTimer) { clearTimeout(addTimer); addTimer = null; }
        syncAdd('idle');
      })
      .then(function () { busy = false; });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var btn = e.target.closest('[data-pqv-open]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      open(btn);
      return;
    }

    // Everything below only applies once the overlay is up.
    if (!root || root.hidden || !root.contains(e.target)) return;
    if (e.target.closest('[data-pqv-close]')) { close(); return; }
    if (e.target.closest('[data-pqv-prev]')) { var gp = gal(); if (gp) gp.go(gp.index() - 1); return; }
    if (e.target.closest('[data-pqv-next]')) { var gn = gal(); if (gn) gn.go(gn.index() + 1); return; }
    if (e.target.closest('[data-pqv-add]')) {
      if (root.dataset.mode === 'build' && root.dataset.build) { window.location.href = root.dataset.build; return; }
      addToCart();
      return;
    }
  });

  /* Plain bubble phase is enough even when the overlay is up over the search modal: the
     shell is its own dialog subtree, so these keys never reach the search's handlers, and
     showModal() makes everything behind the shell inert — the results list cannot steal the
     arrows. (An earlier version needed capture + stopPropagation because the overlay lived
     INSIDE the search dialog; see mountInTopLayer for why it no longer does.) */
  document.addEventListener('keydown', function (e) {
    if (!root || root.hidden) return;
    if (e.key === 'Escape') {
      /* While the shell is up, Escape belongs to the platform: the browser aims its close
         request at the topmost dialog, which is ours, and the shell's `close` event does
         the unwinding. Closing it from here instead dismissed the shell mid-keypress, so
         the browser handed the SAME Escape to the dialog underneath and the search modal
         closed along with the preview. (Traced: close() on .pqv-shell, then `cancel` on
         .search-modal__content within one key press.) */
      if (shell && shell.open) return;
      close();
      return;
    }
    var g = gal();
    if (!g || g.count() < 2) return;
    if (e.key === 'ArrowLeft') { g.go(g.index() - 1); }
    else if (e.key === 'ArrowRight') { g.go(g.index() + 1); }
  });
})();

/*
 * Shared gallery controller for the quick view carousel.
 *
 * Exposed as window.pqvGallery(root) and cached on the root element. Both drivers use it, so
 * slide building, thumbnails, dots and arrow state live here once.
 *
 * The track is a real horizontal scroller with CSS scroll snapping, which is the pattern the
 * theme's other galleries use: the finger drags, the browser snaps, and the active index is
 * read back from scrollLeft rather than tracked by hand. Because every photo is its own
 * <img> on the page, switching cannot flash - there is nothing left to fetch.
 *
 * Resolve it lazily (at first use, not at connect time). This file and bundle-builder.js are
 * both deferred and their execution order is not guaranteed.
 */
window.pqvGallery = function (root) {
  if (!root) return null;
  if (root.pqvGallery) return root.pqvGallery;

  var track = root.querySelector('[data-pqv-track]');
  if (!track) return null;
  var thumbsWrap = root.querySelector('[data-pqv-thumbs]');
  var dotsWrap = root.querySelector('[data-pqv-dots]');
  var prev = root.querySelector('[data-pqv-prev]');
  var next = root.querySelector('[data-pqv-next]');

  var urls = [];
  var alt = '';
  var index = 0;
  var ticking = false;

  function sized(url, w) {
    if (!url) return '';
    return (url.indexOf('?') > -1 ? url.split('?')[0] : url) + '?width=' + w;
  }

  function parseVideo(u) {
    if (typeof u !== 'string' || u.indexOf('video:') !== 0) return null;
    var parts = u.slice(6).split('::');
    return { src: parts[0], poster: parts[1] || '' };
  }

  /* Same photo, any host / size: "…/files/BH_02_LOGO.webp?v=1&width=900" -> "bh_02_logo.webp". */
  function fileKey(u) {
    if (typeof u !== 'string' || u.indexOf('video:') === 0) return '';
    var path = u.split('?')[0];
    return path.slice(path.lastIndexOf('/') + 1).toLowerCase();
  }

  function mark(i) {
    // Fotka, na kterou se právě dojelo, dosedne z mírného přiblížení - stejný detail, jaký má
    // ridgesong. Slide, který není aktivní, drží scale mírně nad 1; jakmile se stane aktivním,
    // přechod ho položí na 1 a oko to čte jako lehké oddálení. Řídí to třída, ne animace, aby
    // se to nespouštělo znovu při každém překreslení scrollu.
    if (track) {
      Array.prototype.forEach.call(track.children, function (sl, n) {
        sl.classList.toggle('is-current', n === i);
        // A video slide plays while it is the one showing and pauses as soon as it is not.
        var v = sl.querySelector('video');
        if (v) {
          if (n === i) { var pr = v.play(); if (pr && pr.catch) pr.catch(function () {}); }
          else v.pause();
        }
      });
    }
    if (thumbsWrap) {
      Array.prototype.forEach.call(thumbsWrap.children, function (b, n) {
        b.classList.toggle('is-active', n === i);
      });
      var active = thumbsWrap.children[i];
      if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest' });
    }
    if (dotsWrap) {
      Array.prototype.forEach.call(dotsWrap.children, function (d, n) {
        d.classList.toggle('is-active', n === i);
      });
    }
  }

  /* Single source of truth for "which photo is showing": where the track is scrolled to. */
  function readIndex() {
    var w = track.clientWidth;
    if (!w) return 0;
    return Math.max(0, Math.min(urls.length - 1, Math.round(track.scrollLeft / w)));
  }

  track.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var i = readIndex();
      if (i !== index) { index = i; mark(i); }
    });
  }, { passive: true });

  function go(i, instant) {
    var n = urls.length;
    if (!n) return;
    var target = ((i % n) + n) % n;   // arrows wrap both ways
    index = target;
    mark(target);
    var left = target * track.clientWidth;
    if (instant) {
      // CSS sets scroll-behavior: smooth, so an instant jump has to opt out explicitly -
      // otherwise reopening the overlay animates across the previous product's gallery.
      var was = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = left;
      track.style.scrollBehavior = was || '';
    } else {
      track.scrollTo({ left: left, behavior: 'smooth' });
    }
  }

  var resetGen = 0;
  var resetRO = null;

  // Doskočení na první fotku, které NEZÁVISÍ na tom, jestli je track už rozložený.
  //
  // `go` počítá `target * track.clientWidth`. Galerie se ale naplní z product.js ve
  // chvíli, kdy modal ještě nemá layout — na mobilu je `clientWidth` v tom okamžiku
  // měřitelně 0 (naměřeno: 362 → 0 → 308 během 120 ms). Zápis do nerozloženého prvku
  // nic nedrží a jakmile track šířku dostane, vybere si `scroll-snap` pozici sám;
  // náhled se pak otevřel třeba na čtvrté fotce. `overflow-anchor: none` tohle
  // neřeší, WebKit tu vlastnost nepodporuje vůbec.
  //
  // Proto se doskočí třikrát: hned, po dalším frame a ještě jednou, až track opravdu
  // dostane šířku. `resetGen` hlídá, aby observer ze starého produktu neškubl
  // galerií, kterou už mezitím prohlíží zákazník.
  function resetToFirst() {
    var gen = ++resetGen;
    go(0, true);
    requestAnimationFrame(function () {
      if (gen === resetGen) go(0, true);
    });

    if (resetRO) { resetRO.disconnect(); resetRO = null; }
    if (track.clientWidth || typeof ResizeObserver !== 'function') return;

    resetRO = new ResizeObserver(function () {
      if (gen !== resetGen || !track.clientWidth) return;
      resetRO.disconnect();
      resetRO = null;
      go(0, true);
    });
    resetRO.observe(track);
    // Kdyby track šířku nikdy nedostal, ať observer nevisí a nezasáhne později.
    setTimeout(function () {
      if (resetRO && gen === resetGen) { resetRO.disconnect(); resetRO = null; }
    }, 1500);
  }

  function set(list, altText, width) {
    urls = (list || []).filter(Boolean);
    alt = altText || '';
    var w = width || 900;

    /* Slides that already show the same file are kept as they are. The card's photo goes in
       first (data-img, on the store's own domain) and product.js arrives ~200 ms later with the
       same photos on cdn.shopify.com - rebuilding the track threw the loaded photo away and
       re-downloaded it under the new URL, so it blinked out and came back at another size. */
    var keep = {};
    Array.prototype.forEach.call(track.children, function (sl) {
      var k = sl.getAttribute('data-file');
      if (k && !keep[k]) keep[k] = sl;
    });
    track.textContent = '';
    urls.forEach(function (u, n) {
      var file = fileKey(u);
      if (file && keep[file]) {
        var kept = keep[file]; delete keep[file];
        var ki = kept.querySelector('img');
        if (ki) { ki.alt = n === 0 ? alt : ''; ki.loading = n === 0 ? 'eager' : 'lazy'; }
        track.appendChild(kept);
        return;
      }
      var slide = document.createElement('div');
      slide.className = 'pqv__slide';
      // "video:<mp4>::<poster>" - a clip with its own play / pause (no native bar). Only the
      // „Sestav si svůj FAR" card sends one; product galleries stay images.
      var vid = parseVideo(u);
      if (vid) {
        slide.classList.add('pqv__slide--video');
        var v = document.createElement('video');
        v.className = 'pqv__vid';
        v.src = vid.src;
        if (vid.poster) v.poster = sized(vid.poster, w);
        v.controls = false;
        v.muted = true;
        v.loop = true;
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.preload = 'none';
        if (alt) v.setAttribute('aria-label', alt);
        slide.appendChild(v);
        // No native bar (as in the gear grid): one round play / pause in the middle. It shows
        // on a tap / pointer move and while paused, and hides 1 s after the clip runs.
        var vb = document.createElement('button');
        vb.type = 'button';
        vb.className = 'pqv__vbtn';
        vb.setAttribute('aria-label', 'Play / pause');
        vb.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path class="pqv__vbtn-play" d="M8 5.5v13l11-6.5z" fill="currentColor"/><g class="pqv__vbtn-pause" fill="currentColor"><rect x="6.5" y="5" width="4" height="14" rx="1"/><rect x="13.5" y="5" width="4" height="14" rx="1"/></g></svg>';
        slide.appendChild(vb);
        (function (sl, vid, btn) {
          var hideT = null;
          function showUi() {
            sl.classList.add('show-ui');
            clearTimeout(hideT);
            if (!vid.paused) hideT = setTimeout(function () { sl.classList.remove('show-ui'); }, 1000);
          }
          vid.addEventListener('play', function () { sl.classList.remove('is-paused'); showUi(); });
          vid.addEventListener('pause', function () { sl.classList.add('is-paused', 'show-ui'); clearTimeout(hideT); });
          btn.addEventListener('click', function (e) {
            e.stopPropagation();
            if (vid.paused) { var pr = vid.play(); if (pr && pr.catch) pr.catch(function () {}); } else vid.pause();
          });
          sl.addEventListener('click', showUi);
          sl.addEventListener('pointermove', showUi);
          sl.classList.add('is-paused', 'show-ui');
        })(slide, v, vb);
        track.appendChild(slide);
        return;
      }
      if (file) slide.setAttribute('data-file', file);
      var im = document.createElement('img');
      im.className = 'pqv__img';
      im.src = sized(u, w);
      im.alt = n === 0 ? alt : '';
      // The first photo is what the customer is waiting for; the rest can wait their turn.
      im.loading = n === 0 ? 'eager' : 'lazy';
      im.decoding = 'async';
      slide.appendChild(im);
      track.appendChild(slide);
    });

    var many = urls.length > 1;

    if (thumbsWrap) {
      thumbsWrap.textContent = '';
      if (many) {
        urls.forEach(function (u, n) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'pqv__thumb';
          b.setAttribute('aria-label', String(n + 1));
          var tv = parseVideo(u);
          var im = document.createElement('img');
          im.src = sized(tv ? tv.poster : u, 140);
          im.alt = '';
          im.loading = 'lazy';
          b.appendChild(im);
          if (tv) {
            b.classList.add('pqv__thumb--video');
            var pl = document.createElement('span');
            pl.className = 'pqv__thumb-play';
            pl.setAttribute('aria-hidden', 'true');
            b.appendChild(pl);
          }
          b.addEventListener('click', function () { go(n); });
          thumbsWrap.appendChild(b);
        });
      }
    }

    if (dotsWrap) {
      dotsWrap.textContent = '';
      if (many) {
        urls.forEach(function (u, n) {
          var d = document.createElement('span');
          d.className = 'pqv__dot';
          dotsWrap.appendChild(d);
        });
      }
    }

    if (prev) prev.hidden = !many;
    if (next) next.hidden = !many;

    resetToFirst();
  }

  root.pqvGallery = { set: set, go: go, index: function () { return index; }, count: function () { return urls.length; } };
  return root.pqvGallery;
};

/*
 * Touch gestures for the mobile sheet, shared by every .pqv instance.
 *
 * Only vertical drags are handled here. Horizontal ones belong to the browser: the track is a
 * CSS scroll-snap carousel, so the finger drags the photos natively and there is nothing for
 * JS to animate.
 *
 * The sheet can be pulled by the handle OR anywhere on the photo, so nobody has to hit the
 * small bar exactly. On the photo the axis is undecided at touchstart - the first few pixels
 * choose: mostly-vertical becomes a sheet drag, mostly-horizontal is handed back to the
 * carousel. The thumbnail strip is excluded, since it is its own vertical scroller.
 *
 * Dismissing is published as pqv:close on the overlay root; expanding and collapsing are
 * pure presentation and stay here.
 */
(function () {
  'use strict';

  var LOCK = 8;        // px before the axis is decided
  var DISMISS = 90;    // px down before the sheet closes
  var EXPAND = 60;     // px up before it goes full screen

  var kind = null;     // 'grab' once committed, 'maybe' while the axis is undecided
  var root = null;
  var dialog = null;
  var x0 = 0;
  var y0 = 0;
  var dy = 0;

  function reset() {
    if (dialog) {
      dialog.classList.remove('is-dragging');
      dialog.style.transform = '';
    }
    kind = null;
    root = null;
    dialog = null;
    dy = 0;
  }

  document.addEventListener('touchstart', function (e) {
    reset();
    if (e.touches.length !== 1 || !e.target.closest) return;
    var r = e.target.closest('[data-pqv]');
    if (!r || r.hidden) return;
    if (e.target.closest('[data-pqv-thumbs]')) return;   // its own scroller
    if (e.target.closest('[data-pqv-grab]')) kind = 'grab';
    else if (e.target.closest('.pqv__media')) kind = 'maybe';
    else return;
    root = r;
    dialog = r.querySelector('.pqv__dialog');
    x0 = e.touches[0].clientX;
    y0 = e.touches[0].clientY;
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!kind || e.touches.length !== 1) return;
    var dx = e.touches[0].clientX - x0;
    dy = e.touches[0].clientY - y0;

    if (kind === 'maybe') {
      if (Math.abs(dx) > LOCK && Math.abs(dx) >= Math.abs(dy)) { reset(); return; }
      if (Math.abs(dy) > LOCK) kind = 'grab';
      else return;
    }

    e.preventDefault();
    if (!dialog) return;
    var expanded = root.classList.contains('pqv--expanded');
    var shift = dy > 0 ? dy : (expanded ? dy * 0.12 : dy * 0.35);
    dialog.classList.add('is-dragging');
    dialog.style.transform = 'translateY(' + shift + 'px)';
  }, { passive: false });

  document.addEventListener('touchend', function () {
    if (kind !== 'grab') { reset(); return; }
    var r = root;
    var down = dy;
    var expanded = r && r.classList.contains('pqv--expanded');
    reset();
    if (!r) return;
    if (down > DISMISS) {
      // Down from full screen returns to the normal sheet first; only then does it close.
      if (expanded) r.classList.remove('pqv--expanded');
      else r.dispatchEvent(new CustomEvent('pqv:close', { bubbles: true }));
    } else if (down < -EXPAND) {
      r.classList.add('pqv--expanded');
    }
  }, { passive: true });
})();

/*
 * Expanding a panel brings it into view.
 *
 * Opening "About this product" at the bottom of the info pane used to leave the customer
 * looking at the summary line with the text they just asked for below the fold. The pane
 * now eases the opened panel up to its top edge.
 *
 * Bound in the capture phase because <details> toggle does not bubble, so ordinary
 * delegation would never see it. Shared by both overlays - the builder's panels and the
 * card's are the same markup.
 */
(function () {
  'use strict';

  document.addEventListener('toggle', function (e) {
    var panel = e.target;
    if (!panel.classList || !panel.classList.contains('pqv__panel') || !panel.open) return;

    var info = panel.closest('.pqv__info');
    if (!info) return;

    // Wait a frame: the panel has just grown, and scrolling to a height that is still
    // being laid out lands in the wrong place.
    requestAnimationFrame(function () {
      var delta = panel.getBoundingClientRect().top - info.getBoundingClientRect().top;
      // Measured against the pane rather than offsetTop, which would depend on whichever
      // ancestor happens to be positioned.
      if (Math.abs(delta) < 2) return;
      info.scrollTo({ top: info.scrollTop + delta - 8, behavior: 'smooth' });
    });
  }, true);
})();
